import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, shareReplay } from "rxjs";
import {
  AssessmentQualitativeOption,
  AssessmentQualitativeOptionResponse,
  IAssessmentItemResponse,
  IBehaviorNineboxResponse,
  INineboxChartTypeResponse,
} from "src/app/pages/performance-manage/models/assesment-item.interface";
import { IAssessmentTimelineResponse } from "src/app/pages/performance-manage/models/assessment-timeline.interface";
import { ICreateCycleRequest } from "src/app/pages/performance-manage/models/create-cycle-request.interface";
import { ICycleResponse } from "src/app/pages/performance-manage/models/cycle.interface";
import { IGrandingSystemResponse } from "src/app/pages/performance-manage/models/granding-system.interface";
import { IAssessmentPanelProgressResponse } from "src/app/pages/performance-manage/panel-of-assessments/model/assessment-panel-progress.interface";
import { IPanelOfAssessmentCycleResponse } from "src/app/pages/performance-manage/panel-of-assessments/model/cycle-response.interface";
import { ICycleYearsFilterResponse } from "src/app/pages/performance-manage/panel-of-assessments/model/cycle-years-filter.interface";
import { environment } from "src/environments/environment";
import { stripNullishParams } from "../helpers/utils/http-params.util";
import { AuthenticationService } from "./auth.service";
import { INineboxConfigurationsResponse } from "src/app/pages/performance-manage/models/ninebox-configurations-response.interface";
import { IAssimilationResponse } from "src/app/pages/performance-manage/models/assimilation-response.interface";
import { IReopenCycleRequest } from "src/app/pages/performance-manage/models/reopen-cycle-request.interface";
import { ICronogramCycleResponse } from "src/app/pages/performance-manage/models/cronogram-cycle-response.interface";
import { IExtendCycleRequest } from "src/app/pages/performance-manage/models/extend-cycle-request.interface";

@Injectable({
  providedIn: "root",
})
export class CycleService {
  cycleAPIUrl = `${environment.url}/v1/cycle`;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) { }

  create(cycle: ICreateCycleRequest): Observable<ICreateCycleRequest> {
    return this.http.post<ICreateCycleRequest>(this.cycleAPIUrl, cycle);
  }

  update(cycle: ICreateCycleRequest): Observable<ICreateCycleRequest> {
    return this.http.put<ICreateCycleRequest>(`${this.cycleAPIUrl}`, cycle);
  }

  delete(cyleId: number): Observable<ICreateCycleRequest> {
    return this.http
      .delete<ICreateCycleRequest>(`${this.cycleAPIUrl}/${cyleId}`)
      .pipe(shareReplay());
  }

  reopen(
    cycleId: number,
    cronogram: IReopenCycleRequest
  ): Observable<IReopenCycleRequest> {
    return this.http
      .post<IReopenCycleRequest>(
        `${this.cycleAPIUrl}/${cycleId}/cronogram/start`,
        cronogram
      )
      .pipe(shareReplay());
  }

  cronogram(cycleId: number): Observable<ICronogramCycleResponse> {
    return this.http.get<ICronogramCycleResponse>(
      `${this.cycleAPIUrl}/${cycleId}/cronogram`
    );
  }

  stop(cycleId: number): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.cycleAPIUrl}/${cycleId}/cronogram/stop`,
      {}
    );
  }

  extend(
    cycleId: number,
    cronogram: IExtendCycleRequest
  ): Observable<IExtendCycleRequest> {
    return this.http
      .post<IReopenCycleRequest>(
        `${this.cycleAPIUrl}/${cycleId}/cronogram/extend`,
        cronogram
      )
      .pipe(shareReplay());
  }

  getAll(query: any): Observable<ICycleResponse> {
    return this.http.get<ICycleResponse>(`${this.cycleAPIUrl}`, {
      params: stripNullishParams({ ...query }),
    });
  }

  getAssessmentTimeline(id: number): Observable<IAssessmentTimelineResponse> {
    return this.http.get<IAssessmentTimelineResponse>(
      `${this.cycleAPIUrl}/assesment-timeline?CycleId=${id}`
    );
  }

  getCycleAssessments(
    id: number,
    query: any
  ): Observable<IAssessmentPanelProgressResponse> {
    return this.http.get<IAssessmentPanelProgressResponse>(
      `${this.cycleAPIUrl}/${id}/panel`,
      {
        params: stripNullishParams({ ...query }),
      }
    );
  }

  getAssessmentPanel(query: any): Observable<IPanelOfAssessmentCycleResponse> {
    return this.http.get<IPanelOfAssessmentCycleResponse>(
      `${this.cycleAPIUrl}/panel`,
      {
        params: { ...query },
      }
    );
  }

  getYears(): Observable<ICycleYearsFilterResponse> {
    return this.http.get<ICycleYearsFilterResponse>(
      `${this.cycleAPIUrl}/years`
    );
  }

  getById(id: number, query: object): Observable<any> {
    return this.http.get<any>(`${this.cycleAPIUrl}/${id}`, {
      params: stripNullishParams({ ...query } as Record<string, unknown>) as Record<string, string | number | boolean | (string | number | boolean)[]>,
    });
  }

  getGrandingSystem(): Observable<IGrandingSystemResponse> {
    return this.http.get<IGrandingSystemResponse>(
      `${this.cycleAPIUrl}/granding-system`
    );
  }

  getAssesmentItens(): Observable<IAssessmentItemResponse> {
    return this.http.get<IAssessmentItemResponse>(
      `${this.cycleAPIUrl}/assesment-itens`
    );
  }

  getQualitativeOptions(): Observable<AssessmentQualitativeOptionResponse> {
    return this.http.get<AssessmentQualitativeOptionResponse>(
      `${this.cycleAPIUrl}/qualitative-option`
    );
  }

  getBehaviorNinebox(): Observable<IBehaviorNineboxResponse> {
    return this.http.get<IBehaviorNineboxResponse>(
      `${this.cycleAPIUrl}/behavior-ninebox`
    );
  }

  getNineboxChartType(): Observable<INineboxChartTypeResponse> {
    return this.http.get<INineboxChartTypeResponse>(
      `${this.cycleAPIUrl}/ninebox-chart-type`
    );
  }

  getAssimiliation(query: object): Observable<IAssimilationResponse> {
    return this.http.get<IAssimilationResponse>(
      `${this.cycleAPIUrl}/ninebox/assimilations`,
      {
        params: { ...query },
      }
    );
  }

  getConfigurations(query: any): Observable<INineboxConfigurationsResponse> {
    return this.http.get<INineboxConfigurationsResponse>(
      `${this.cycleAPIUrl}/ninebox/configurations`,
      {
        params: stripNullishParams({ ...query }),
      }
    );
  }

  removeDraftFromLocalStorage(cycle: any, cnpj: string): void {
    const drafts = JSON.parse(localStorage.getItem(cnpj)) || [];
    drafts.items = drafts.items.filter(
      (draft: any) => draft.draftId !== cycle.draftId
    );
    localStorage.setItem(cnpj, JSON.stringify(drafts));
  }
}
